import { useEffect } from 'react';

import { TRACKED_ERRORS } from './constants';
import { trackError } from './sendEvent';

type Props = {
  downloadsError?: Error;
  videoFilesError?: Error;
  clipMetadataError?: Error;
  videoID?: string;
  versionId?: number;
  selectedAudioTrack: string;
  allowAudioTracksSelector: boolean;
  isNoLinksToShow?: boolean;
  isFallbackLinksUsed?: boolean;
  versionIsMissing?: boolean;
};

export function useErrorTracking({
  versionIsMissing,
  clipMetadataError,
  videoFilesError,
  downloadsError,
  videoID,
  versionId,
  selectedAudioTrack,
  isFallbackLinksUsed,
  isNoLinksToShow,
  allowAudioTracksSelector,
}: Props) {
  useEffect(() => {
    if (allowAudioTracksSelector && !videoID) {
      const hint = 'Video ID is missing.';
      trackError(new Error(hint), {
        versionId,
        videoID,
        hint,
        trackingTag: TRACKED_ERRORS.VIDEO_ID_MISSING,
      });
    }
  }, [videoID, versionId, allowAudioTracksSelector]);

  useEffect(() => {
    if (downloadsError) {
      const trackId = selectedAudioTrack.split('/').pop();
      trackError(downloadsError, {
        videoID,
        versionId,
        selectedAudioTrack,
        trackId,
        hint: 'Failed to fetch DL links for video and audio-track.',
        method: 'useGetVideoVersionDownloads',
        trackingTag: TRACKED_ERRORS.FETCH_DL_LINKS_FAILED,
      });
    }
  }, [selectedAudioTrack, downloadsError, videoID, versionId]);

  useEffect(() => {
    if (videoFilesError) {
      trackError(videoFilesError, {
        videoID,
        hint: 'Failed to fetch main DL links for video.',
        method: 'useGetVideo',
        trackingTag: TRACKED_ERRORS.FETCH_CLIP_FAILED,
      });
    }
  }, [videoFilesError, videoID]);

  useEffect(() => {
    if (allowAudioTracksSelector && clipMetadataError) {
      trackError(clipMetadataError, {
        versionId,
        videoID,
        hint: "Failed to fetch clip's metadata.",
        method: 'useGetVideoMetadata',
        trackingTag: TRACKED_ERRORS.FETCH_CLIP_METADATA_FAILED,
      });
    }
  }, [allowAudioTracksSelector, clipMetadataError, versionId, videoID]);

  useEffect(() => {
    if (allowAudioTracksSelector && versionIsMissing) {
      const hint = 'Version ID is missing.';
      trackError(new Error(hint), {
        versionIsMissing,
        videoID,
        hint,
        trackingTag: TRACKED_ERRORS.VIDEO_VERSION_ID_MISSING,
      });
    }
  }, [allowAudioTracksSelector, versionIsMissing, videoID]);

  useEffect(() => {
    if (allowAudioTracksSelector && (isNoLinksToShow || isFallbackLinksUsed)) {
      const hint = isNoLinksToShow ? 'No download links to show.' : 'Used fallback links.';

      trackError(new Error(hint), {
        videoID,
        versionId,
        isNoLinksToShow,
        isFallbackLinksUsed,
        selectedAudioTrack,
        trackingTag: isNoLinksToShow
          ? TRACKED_ERRORS.UI_NO_LINKS_TO_SHOW
          : TRACKED_ERRORS.UI_USED_FALLBACK_LINKS,
      });
    }
  }, [
    allowAudioTracksSelector,
    isNoLinksToShow,
    versionId,
    videoID,
    selectedAudioTrack,
    isFallbackLinksUsed,
  ]);
}
